import React from "react";
import { motion } from "framer-motion";
import { Boxes, Factory, HeartPulse, Warehouse, ShoppingBag, Trophy, Database, Bot } from "lucide-react";
import { SectionHeading } from "./Section";
import { TiltCard, Reveal } from "../../lib/ui-motion";
import { useLang } from "../../i18n";

const META = {
  platform: { icon: Boxes, span: "lg:col-span-2 lg:row-span-2", big: true },
  factory: { icon: Factory, span: "lg:col-span-1" },
  health: { icon: HeartPulse, span: "lg:col-span-1" },
  warehouse: { icon: Warehouse, span: "lg:col-span-2" },
  ecom: { icon: ShoppingBag, span: "lg:col-span-1" },
  sports: { icon: Trophy, span: "lg:col-span-1" },
  crm: { icon: Database, span: "lg:col-span-2" },
  agent: { icon: Bot, span: "lg:col-span-2" },
};

const Bars = () => (
  <div className="flex items-end gap-1.5 h-16">
    {[0.4, 0.7, 0.5, 0.9, 0.65, 1].map((v, i) => (
      <motion.span
        key={i}
        className="w-3 rounded-sm bg-flame/70"
        initial={{ height: 4 }}
        whileInView={{ height: `${v * 100}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
      />
    ))}
  </div>
);

const Nodes = () => (
  <svg viewBox="0 0 120 70" className="w-full h-20">
    {[[20, 50], [60, 20], [100, 45], [55, 60]].map((p, i) => (
      <motion.circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill="#FF6A00"
        animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 2.4, delay: i * 0.4 }} />
    ))}
    <path d="M20 50 L60 20 L100 45 L55 60 Z" stroke="rgba(255,106,0,0.35)" strokeWidth="1" fill="none" />
    <path d="M60 20 L55 60" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
  </svg>
);

export const WhatIBuild = () => {
  const { t } = useLang();
  const b = t.build;

  return (
    <section id="build" className="relative py-24 md:py-36" data-testid="build-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHeading overline={b.overline} title={b.title} />

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[200px] grid-flow-dense">
          {b.cards.map((c, i) => {
            const meta = META[c.k] || { icon: Boxes, span: "" };
            const Icon = meta.icon;
            return (
              <Reveal key={i} delay={i * 0.04} className={meta.span}>
                <TiltCard
                  max={6}
                  data-cursor="hover"
                  data-testid={`build-card-${c.k}`}
                  className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-ink-surface p-6 md:p-8 transition-colors duration-500 hover:border-flame/30"
                >
                  <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-flame/0 blur-3xl transition-all duration-500 group-hover:bg-flame/15" />
                  <div className="relative flex h-full flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-flame transition-colors group-hover:bg-flame group-hover:text-black">
                        <Icon size={22} />
                      </span>
                      {meta.big && <Nodes />}
                    </div>
                    <div>
                      {meta.big && <div className="mb-6"><Bars /></div>}
                      <h3 className={`font-heading font-bold ${meta.big ? "text-3xl md:text-4xl" : "text-xl"}`}>{c.t}</h3>
                      <p className="mt-2 text-sm text-white/50 leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>

        {/* integrations marquee */}
        <div className="relative mt-12 overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10" />
          <div className="flex w-max animate-marquee gap-4">
            {[...b.integrations, ...b.integrations, ...b.integrations].map((n, i) => (
              <span key={i} className="whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-6 py-2.5 font-heading font-semibold text-white/60">
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
